/**
 * AppContent: routing logic and page rendering.
 * Rendered inside AuthGuard + CatalogProvider in App.tsx.
 * Consumes catalog state via useCatalog() and handles hash-based navigation.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import CatalogPage from "./pages/CatalogPage";
import PluginDetailPage from "./pages/PluginDetailPage";
import ContributeSkillPage from "./pages/ContributeSkillPage";
import AdminPage from "./pages/AdminPage";
import HelpPage from "./pages/HelpPage";
import ThemeToggle from "./components/ThemeToggle";
import type { EditData } from "./pages/ContributeSkillPage";
import { useAuth } from "./lib/auth-context";
import { env, IS_DEV } from "./lib/env";
import { useCatalog, installKeyForPlugin, findPluginByInstallKey } from "./lib/catalog-context";
import { fetchSkillTree, fetchSkillBlob, checkAdminStatus, legacyInstallKey } from "./lib/github-proxy-client";
import { reportError } from "./lib/newrelic";
import { trackPageView } from "./lib/gtm";
import type { ExtensionType, MarketplaceEntry, User } from "./types/index";

import { parseHash, routeToHash, type Route } from "./lib/routing";
import { resolvePageTitle } from "./lib/page-title";
import { computeEffectiveBreadcrumbs } from "./lib/parent-plugin";

export default function AppContent() {
  const { plugins, findByInstallKey, parentIndex, removePlugin } = useCatalog();
  const { user: authUser, logout } = useAuth();

  const [route, setRoute] = useState<Route>(() => parseHash(window.location.hash));
  const [catalogNavKey, setCatalogNavKey] = useState(0);

  const [devEmail, setDevEmail] = useState("");
  const [devName, setDevName] = useState("");

  const entraUser = env.REACT_APP_ENTRA_CLIENT_ID ? authUser : null;
  const devUser: User | null =
    IS_DEV && !entraUser && devEmail
      ? { id: "dev", email: devEmail, name: devName || devEmail }
      : null;
  const user = entraUser ?? devUser;

  const [isAdmin, setIsAdmin] = useState(false);

  const [helpFromSkill, setHelpFromSkill] = useState(false);

  const [editData, setEditData] = useState<EditData | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");

  useEffect(() => {
    if (user?.email) {
      checkAdminStatus().then(setIsAdmin).catch(() => setIsAdmin(false));
    }
  }, [user?.email]);

  const navigate = useCallback((next: Route) => {
    window.location.hash = routeToHash(next);
  }, []);

  // onHashChange fires the GA pageview synchronously, before React re-renders, so it
  // can't read selectedPlugin (derived during render). Resolve the plugin from a ref
  // here to give the pageview a meaningful title for in-app navigation; the title
  // effect below refines it once a deep-linked detail page's plugin resolves.
  const pluginsRef = useRef(plugins);
  pluginsRef.current = plugins;

  useEffect(() => {
    const onHashChange = () => {
      const next = parseHash(window.location.hash);
      if (next.page !== "help") setHelpFromSkill(false);
      setRoute(next);
      // Use pluginsRef (always current) rather than the stale findByInstallKey closure —
      // the hashchange fires synchronously, before React re-renders, so the context
      // value captured at effect-setup time would return null for any plugin that
      // arrived after mount. Mirrors the old catalogRef pattern in App.tsx.
      const plugin =
        next.page === "detail" || next.page === "edit"
          ? findPluginByInstallKey(pluginsRef.current, next.installKey)
          : null;
      const title = resolvePageTitle(next, plugin);
      document.title = title;
      trackPageView(window.location.hash, title);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const editInstallKey = route.page === "edit" ? route.installKey : null;

  useEffect(() => {
    if (!editInstallKey) {
      setEditData(null);
      setEditError("");
      return;
    }

    const plugin = plugins.find(
      (p) => installKeyForPlugin(p).toLowerCase() === editInstallKey.toLowerCase(),
    );
    if (!plugin) {
      setEditError("Not found in catalog");
      return;
    }

    let cancelled = false;
    setEditLoading(true);
    setEditError("");

    // github-proxy decodes source/org/repo from the legacy install-key shape;
    // the marketplace's installName (= manifestName post #176) isn't parseable.
    const proxyKey = legacyInstallKey(plugin);

    fetchSkillTree(proxyKey, plugin.lastCommitSha, plugin.skillPath)
      .then(async (tree) => {
        if (cancelled) return;
        const blobs = await Promise.all(
          tree.files.map(async (f) => {
            try {
              const blob = await fetchSkillBlob(proxyKey, f.sha);
              const content =
                blob.encoding === "base64"
                  ? blob.content
                  : btoa(
                      new TextEncoder()
                        .encode(blob.content)
                        .reduce((s, b) => s + String.fromCharCode(b), ""),
                    );
              return { name: f.path.split("/").pop()!, path: f.path, content };
            } catch {
              return null;
            }
          }),
        );
        if (cancelled) return;
        const validBlobs = blobs.filter((b): b is NonNullable<typeof b> => b !== null);

        let userKeywords = plugin.keywords;
        const manifestBlob = validBlobs.find((b) => b.path.endsWith("plugin.json"));
        if (manifestBlob) {
          try {
            const manifest = JSON.parse(atob(manifestBlob.content));
            if (Array.isArray(manifest.keywords)) {
              userKeywords = manifest.keywords;
            }
          } catch {
            // fall back to catalog keywords
          }
        }

        setEditData({
          // installKey on editData is sent back to github-proxy as
          // `previousInstallKey` for rename detection, so use the legacy shape.
          installKey: proxyKey,
          skillName: plugin.pluginName,
          description: plugin.description,
          keywords: userKeywords,
          existingFiles: validBlobs,
        });
      })
      .catch((err: unknown) => {
        // Report before the cancelled guard so navigation-away errors
        // (e.g., a github-proxy 5xx during route change) still reach NR.
        reportError(err, "edit-skill-files-load");
        if (cancelled) return;
        setEditData(null);
        setEditError(err instanceof Error ? err.message : "Failed to load files");
      })
      .finally(() => {
        if (!cancelled) setEditLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [editInstallKey, plugins]);

  const handleSelectPlugin = (plugin: MarketplaceEntry) => {
    navigate({ page: "detail", installKey: installKeyForPlugin(plugin) });
  };

  const handleBackToCatalog = useCallback(() => {
    const catalogRoute: Route = { page: "catalog" };
    setRoute(catalogRoute);
    navigate(catalogRoute);
    setCatalogNavKey((k) => k + 1);
  }, [navigate]);

  // The trail is derived from the route (see computeEffectiveBreadcrumbs), so a
  // crumb click is just a navigation — there's no stack to slice. index is unused.
  const handleBreadcrumbClick = useCallback(
    (plugin: MarketplaceEntry) => {
      navigate({ page: "detail", installKey: installKeyForPlugin(plugin) });
    },
    [navigate],
  );

  const navigateToHelp = useCallback(() => {
    setHelpFromSkill(route.page === "detail");
    navigate({ page: "help" });
  }, [route.page, navigate]);

  const replaceFilters = useCallback(
    (
      query: string,
      clients: string[],
      categories: string[],
      type?: ExtensionType,
      official?: boolean,
    ) => {
      const hash = routeToHash({
        page: "catalog",
        filterClients: clients.length > 0 ? clients : undefined,
        filterCategories: categories.length > 0 ? categories : undefined,
        filterQuery: query || undefined,
        filterType: type,
        filterOfficial: official || undefined,
      });
      window.history.replaceState(null, "", hash);
    },
    [],
  );

  const handleLogout = () => {
    logout()
      .catch((error) => console.error("Logout failed:", error))
      .finally(() => window.location.reload());
  };

  const selectedPlugin =
    route.page === "detail" || route.page === "edit"
      ? findByInstallKey(route.installKey)
      : null;

  // Resolve the plugin a derived skill came from, so its detail page shows the
  // parent reference regardless of entry point (direct catalog click, deep link).
  const parentPlugin = selectedPlugin?.installName
    ? parentIndex.get(selectedPlugin.installName)
    : undefined;

  const effectiveBreadcrumbs = computeEffectiveBreadcrumbs(parentPlugin);

  // Keep the document/tab title in sync with the active route. selectedPlugin
  // resolves asynchronously after the catalog loads, so a deep link to a detail
  // page relies on this to correct the title once the plugin is known.
  // onHashChange owns the GA pageview; this effect only refines the title.
  useEffect(() => {
    document.title = resolvePageTitle(route, selectedPlugin);
  }, [route, selectedPlugin]);

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "var(--color-bg-body)" }}>
      {/* Header */}
      <header
        style={{
          backgroundColor: "var(--color-surface)",
          borderBottom: "1px solid var(--color-border-header)",
          padding: "1rem 2rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h1 style={{ margin: 0 }}>
          <button
            onClick={handleBackToCatalog}
            style={{
              background: "none",
              border: "none",
              color: "var(--color-brand)",
              cursor: "pointer",
              fontSize: "1.5rem",
              fontWeight: "bold",
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ verticalAlign: "middle", marginRight: "0.4rem" }}
            >
              <rect x="3" y="4" width="18" height="16" rx="3" />
              <circle cx="9" cy="12" r="1.5" />
              <circle cx="15" cy="12" r="1.5" />
              <path d="M9 16h6" />
              <path d="M2 10h-1" />
              <path d="M23 10h-1" />
            </svg>
            AI Marketplace
          </button>
        </h1>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <ThemeToggle />
          <button
            onClick={() => navigate({ page: "contribute" })}
            style={{
              padding: "0.5rem 1rem",
              fontSize: "0.9rem",
              backgroundColor:
                route.page === "contribute" || route.page === "edit"
                  ? "var(--color-brand-light)"
                  : "var(--color-brand-emphasis)",
              color:
                route.page === "contribute" || route.page === "edit"
                  ? "var(--color-brand)"
                  : "var(--color-on-brand)",
              border:
                route.page === "contribute" || route.page === "edit"
                  ? "1px solid var(--color-border)"
                  : "1px solid var(--color-brand-emphasis)",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Contribute
          </button>
          <button
            onClick={navigateToHelp}
            style={{
              padding: "0.5rem 1rem",
              fontSize: "0.9rem",
              backgroundColor: route.page === "help" ? "var(--color-brand-light)" : "transparent",
              color: route.page === "help" ? "var(--color-brand)" : "var(--color-text-secondary)",
              border: "1px solid var(--color-border)",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Help
          </button>
          {isAdmin && (
            <button
              onClick={() => navigate({ page: "admin" })}
              style={{
                padding: "0.5rem 1rem",
                fontSize: "0.9rem",
                backgroundColor: route.page === "admin" ? "var(--color-brand-light)" : "transparent",
                color: route.page === "admin" ? "var(--color-brand)" : "var(--color-text-secondary)",
                border: "1px solid var(--color-border)",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Admin
            </button>
          )}
          {user && (
            <span style={{ color: "var(--color-text-secondary)", fontSize: "0.9rem" }}>
              {user.name || user.email}
            </span>
          )}
          <button
            onClick={handleLogout}
            style={{
              padding: "0.5rem 1rem",
              fontSize: "0.9rem",
              backgroundColor: "var(--color-surface-secondary)",
              color: "var(--color-text-primary)",
              border: "1px solid var(--color-border)",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Sign out
          </button>
        </div>
      </header>

      {/* Dev identity (shown in dev mode when no Entra user) */}
      {IS_DEV && !entraUser && (
        <div
          style={{
            backgroundColor: "var(--color-warning-bg)",
            borderBottom: "1px dashed var(--color-warning-border)",
            padding: "0.5rem 2rem",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            fontSize: "0.8rem",
            color: "var(--color-warning-text)",
          }}
        >
          <strong>Dev identity:</strong>
          <input
            type="text"
            value={devName}
            onChange={(e) => setDevName(e.target.value)}
            placeholder="Name"
            style={{
              padding: "0.25rem 0.5rem",
              fontSize: "0.8rem",
              border: "1px solid var(--color-border)",
              borderRadius: "4px",
              width: "120px",
            }}
          />
          <input
            type="email"
            value={devEmail}
            onChange={(e) => setDevEmail(e.target.value)}
            placeholder="Email"
            style={{
              padding: "0.25rem 0.5rem",
              fontSize: "0.8rem",
              border: "1px solid var(--color-border)",
              borderRadius: "4px",
              width: "180px",
            }}
          />
        </div>
      )}

      {/* Main content */}
      <main style={{ maxWidth: "1200px", margin: "0 auto", padding: "2rem" }}>
        {route.page === "catalog" && (
          <CatalogPage
            key={catalogNavKey}
            onSelectPlugin={handleSelectPlugin}
            initialClients={route.filterClients}
            initialCategories={route.filterCategories}
            initialQuery={route.filterQuery}
            initialType={route.filterType}
            initialOfficial={route.filterOfficial}
            onHelp={navigateToHelp}
            onFiltersChange={replaceFilters}
          />
        )}

        {route.page === "detail" && selectedPlugin && (
          <PluginDetailPage
            plugin={selectedPlugin}
            breadcrumbs={effectiveBreadcrumbs}
            parentPlugin={parentPlugin}
            onBackToCatalog={handleBackToCatalog}
            onBreadcrumbClick={handleBreadcrumbClick}
            onNavigateToPlugin={(plugin) => {
              navigate({ page: "detail", installKey: installKeyForPlugin(plugin) });
            }}
            findPlugin={(installName) =>
              findByInstallKey(installName) ?? undefined
            }
            user={user}
            onEdit={() =>
              navigate({ page: "edit", installKey: installKeyForPlugin(selectedPlugin) })
            }
            onDelete={() => {
              removePlugin(installKeyForPlugin(selectedPlugin));
              handleBackToCatalog();
            }}
            onClientClick={(client) =>
              navigate({ page: "catalog", filterClients: [client] })
            }
            onCategoryClick={(category) =>
              navigate({ page: "catalog", filterCategories: [category] })
            }
            onKeywordClick={(keyword) =>
              navigate({ page: "catalog", filterQuery: keyword })
            }
            onHelp={navigateToHelp}
          />
        )}

        {route.page === "detail" && !selectedPlugin && plugins.length > 0 && (
          <div style={{ textAlign: "center", padding: "2rem 0" }}>
            <h2 style={{ color: "var(--color-text-primary)" }}>Not found</h2>
            <p style={{ color: "var(--color-text-secondary)" }}>
              The plug-in or skill you&apos;re looking for doesn&apos;t exist or has been removed.
            </p>
            <button
              onClick={handleBackToCatalog}
              style={{
                marginTop: "1rem",
                padding: "0.5rem 1.5rem",
                fontSize: "1rem",
                backgroundColor: "var(--color-surface-secondary)",
                color: "var(--color-text-primary)",
                border: "1px solid var(--color-border)",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Back to Marketplace
            </button>
          </div>
        )}

        {route.page === "edit" && editLoading && (
          <div style={{ textAlign: "center", padding: "2rem 0" }}>
            <p style={{ color: "var(--color-text-secondary)" }}>Loading details...</p>
          </div>
        )}

        {route.page === "edit" && editError && (
          <div style={{ textAlign: "center", padding: "2rem 0" }}>
            <h2 style={{ color: "var(--color-text-primary)" }}>Failed to load</h2>
            <p style={{ color: "var(--color-text-secondary)" }}>{editError}</p>
            <button
              onClick={handleBackToCatalog}
              style={{
                marginTop: "1rem",
                padding: "0.5rem 1.5rem",
                fontSize: "1rem",
                backgroundColor: "var(--color-surface-secondary)",
                color: "var(--color-text-primary)",
                border: "1px solid var(--color-border)",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Back to Marketplace
            </button>
          </div>
        )}

        {route.page === "edit" && editData && !editLoading && (
          <ContributeSkillPage
            onBack={handleBackToCatalog}
            onViewSkill={(installName) =>
              navigate({ page: "detail", installKey: installName })
            }
            user={user}
            editData={editData}
          />
        )}

        {route.page === "contribute" && (
          <ContributeSkillPage
            onBack={handleBackToCatalog}
            onViewSkill={(installName) =>
              navigate({ page: "detail", installKey: installName })
            }
            user={user}
          />
        )}

        {route.page === "help" && (
          <HelpPage
            onBack={helpFromSkill ? () => window.history.back() : handleBackToCatalog}
            backLabel={helpFromSkill ? "Back to Detail" : "Back to Marketplace"}
            onContribute={() => navigate({ page: "contribute" })}
          />
        )}

        {route.page === "admin" && user && (
          <AdminPage onBack={handleBackToCatalog} />
        )}
      </main>

      {/* Footer */}
      <footer
        style={{
          backgroundColor: "var(--color-surface-page)",
          borderTop: "1px solid var(--color-border-header)",
          padding: "2rem",
          textAlign: "center",
          color: "var(--color-text-muted)",
          fontSize: "0.9rem",
          marginTop: "2rem",
        }}
      >
        <p style={{ margin: 0 }}>AI Marketplace © 2026 • Cox Automotive</p>
      </footer>
    </div>
  );
}
